import React from 'react';
import type { QuoteListRow } from '../fixtures';
import { laneAllInTotal } from '../laneSummary';
import { statusBadgeClass } from '../quoteColors';

/* ──────────────────────────────────────────────────────────────────────────
 * LaneTable — the 12-column lane list inside a (customer, bill-to) group
 * (Quotes — Lane Table + Editor Drawer, Task A). Replaces the collapse/expand
 * QuoteLaneRow cards with a flat TABLE. Pure render from the grouped-search
 * payload: every cell comes off the `QuoteListRow` props already in hand — NO
 * getQuote, NO network. Clicking a row body opens the editor drawer (the parent
 * routes DIRECT vs WAREHOUSE off the lane it receives in onOpenLane); the
 * leading checkbox selects the lane for bulk actions.
 * ────────────────────────────────────────────────────────────────────────── */

export interface LaneTableProps {
  lanes: QuoteListRow[];
  /** Row-body click / keyboard activate — open the editor drawer for this lane. */
  onOpenLane: (lane: QuoteListRow) => void;
  /** quoteId refs currently selected for bulk actions. */
  selectedRefs?: Set<string>;
  onToggleLane?: (lane: QuoteListRow, checked: boolean) => void;
  /** Optional select-all header checkbox. */
  onToggleAll?: (checked: boolean) => void;
}

/** Format the all-in total as "$1,037.00"; "—" when not computable. */
function formatTotal(n: number | null): string {
  if (n == null) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** "40 HC" style equipment summary from size + equipType. */
function equipSummary(lane: QuoteListRow): string {
  return [lane.size, lane.equipType].filter(Boolean).join(' ');
}

/** "40 HC · IMPORT" — equipment summary plus the move type (either half may be absent). */
function equipMove(lane: QuoteListRow): string {
  const equip = equipSummary(lane);
  return [equip, lane.moveType].filter(Boolean).join(' · ');
}

/** Base dray rate as "$850.00"; "—" when absent (matches QuoteLaneRow's "$<raw>"). */
function formatDray(raw: string | undefined): string {
  return raw ? `$${raw}` : '—';
}

/** "06/17/2026 · 2" — last-used date plus days-out; either half may be absent. */
function lastUsedDays(lane: QuoteListRow): string {
  const date = lane.lastUsedDate || '';
  const days = lane.daysOut != null ? String(lane.daysOut) : '';
  return [date, days].filter(Boolean).join(' · ') || '—';
}

const TH = 'px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap';
const TD = 'px-3 py-2 align-top text-slate-700 dark:text-slate-300 whitespace-nowrap';

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`px-2 py-[2px] text-[0.625rem] font-bold uppercase tracking-wider rounded border ${statusBadgeClass(status)}`}>
    {status}
  </span>
);

const LaneTable: React.FC<LaneTableProps> = ({ lanes, onOpenLane, selectedRefs, onToggleLane, onToggleAll }) => {
  const allSelected = lanes.length > 0 && lanes.every((l) => selectedRefs?.has(l.quoteId));

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[0.78125rem] border-collapse">
        <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
          <tr>
            <th scope="col" className={`${TH} w-8`}>
              {onToggleAll && (
                <input
                  type="checkbox"
                  aria-label="select all quotes"
                  checked={allSelected}
                  onChange={(e) => onToggleAll(e.target.checked)}
                />
              )}
            </th>
            <th scope="col" className={TH}>Quote #</th>
            <th scope="col" className={TH}>Lane</th>
            <th scope="col" className={TH}>Equip · Move</th>
            <th scope="col" className={TH}>Dray</th>
            <th scope="col" className={TH}>Total</th>
            <th scope="col" className={TH}>Status</th>
            <th scope="col" className={TH}>Effective</th>
            <th scope="col" className={TH}>End Date</th>
            <th scope="col" className={TH}>Last Used / Days</th>
            <th scope="col" className={TH}>Sold By</th>
            <th scope="col" className={TH}>Create Date</th>
          </tr>
        </thead>
        <tbody>
          {lanes.map((lane) => {
            const isWhse = lane.quoteType !== 'DIRECT';
            const selected = selectedRefs?.has(lane.quoteId);
            const open = () => onOpenLane(lane);
            return (
              <tr
                key={lane.quoteId}
                data-testid="lane-row"
                className={[
                  'border-b border-slate-100 dark:border-slate-700 cursor-pointer',
                  'hover:bg-slate-50 dark:hover:bg-slate-800/60',
                  selected ? 'bg-blue-50/60 dark:bg-blue-900/20' : '',
                ].join(' ')}
              >
                {/* Select checkbox — stops propagation so it never opens the drawer. */}
                <td className={`${TD} w-8`} onClick={(e) => e.stopPropagation()}>
                  {onToggleLane && <input
                    type="checkbox"
                    aria-label={`select quote ${lane.lane}`}
                    checked={selected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onToggleLane(lane, e.target.checked)}
                  />}
                </td>

                {/* Quote # — the focusable row-opener (button → keyboard accessible). */}
                <td className={TD}>
                  <button
                    type="button"
                    onClick={open}
                    aria-label={`open quote ${lane.lane}`}
                    className="font-semibold text-blue-700 dark:text-blue-400 hover:underline text-left"
                  >
                    {lane.quoteId}
                  </button>
                </td>

                {/* Remaining cells are also clickable (open the drawer). */}
                <td className={`${TD} font-medium text-slate-900 dark:text-slate-100`} onClick={open}>
                  {lane.lane}
                  {isWhse && (
                    <span className="ml-2 px-1.5 py-[1px] text-[0.5625rem] font-bold uppercase tracking-wider rounded bg-indigo-100 text-indigo-700 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700">
                      WHSE
                    </span>
                  )}
                </td>
                <td className={TD} onClick={open}>{equipMove(lane) || '—'}</td>
                <td className={TD} onClick={open}>{formatDray(lane.drayRate)}</td>
                <td className={`${TD} font-semibold text-slate-900 dark:text-slate-100`} onClick={open}>
                  {formatTotal(laneAllInTotal(lane))}
                </td>
                <td className={TD} onClick={open}><StatusBadge status={lane.status} /></td>
                <td className={TD} onClick={open}>{lane.effective || '—'}</td>
                <td className={TD} onClick={open}>{lane.expires || '—'}</td>
                <td className={TD} onClick={open}>{lastUsedDays(lane)}</td>
                <td className={TD} onClick={open}>{lane.soldBy || '—'}</td>
                <td className={TD} onClick={open}>{lane.createdDate || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default LaneTable;
